/**
 * Bundle Cart Banner
 *
 * 1) PRUNE: Removes stale banners when bundle items are deleted from cart.
 *
 * 2) APPLY: Collects ALL bundle discount codes from visible banners +
 *    any already-applied discounts, then sends them comma-separated via
 *    /cart/update.js ({ discount: 'BNRUNNER,BNDECOR' }) so multiple
 *    bundle discounts coexist.
 */

/* ── Prune banners when line items change ── */

async function recountAndPruneBundles(cart) {
  const banners = document.querySelectorAll('[data-bundle-cart-banner]');
  if (!banners.length) return;

  const codeCounts = Object.create(null);
  if (cart?.items) {
    for (const item of cart.items) {
      const code = item?.properties?._bundle_campaign;
      if (code) codeCounts[code] = (codeCounts[code] || 0) + 1;
    }
  }

  const removedCodes = [];

  for (const banner of banners) {
    const code = banner.dataset.code;
    if (!code || (codeCounts[code] || 0) < 2) {
      removedCodes.push(code);
      banner.remove();
    }
  }

  // Remove orphaned discount codes from cart.
  if (removedCodes.length > 0) {
    const appliedCodes = (cart.discount_applications || [])
      .filter(d => d.type === 'discount_code')
      .map(d => d.title);

    const remaining = appliedCodes.filter(c => !removedCodes.includes(c));

    // Only update if codes actually changed.
    if (remaining.length !== appliedCodes.length) {
      const cartUpdateUrl = window.theme?.routes?.cartUpdate || '/cart/update.js';
      try {
        await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ discount: remaining.join(',') })
        });
      } catch (err) {
        console.error('[bundle-cart-banner] discount removal failed:', err);
      }
    }
  }
}

document.addEventListener('on:line-item:change', (event) => {
  if (event?.detail?.cart) recountAndPruneBundles(event.detail.cart);
});

/* ── AJAX discount code application via /cart/update.js ── */

async function applyBundleDiscount(button) {
  const newCode = button.dataset.applyDiscount;
  if (!newCode) return;

  const banner = button.closest('[data-bundle-cart-banner]');
  const cartUpdateUrl = window.theme?.routes?.cartUpdate || '/cart/update.js';
  const cartUrl = window.theme?.routes?.cart || '/cart.js';

  button.disabled = true;
  button.textContent = 'Applying…';

  try {
    // Fetch current cart JSON to get already-applied discount codes.
    const cartResponse = await fetch(cartUrl + '.js', { credentials: 'same-origin' });
    const cart = await cartResponse.json();

    // Collect existing discount codes + the new one.
    const existingCodes = (cart.discount_applications || [])
      .filter(d => d.type === 'discount_code')
      .map(d => d.title);

    if (!existingCodes.includes(newCode)) {
      existingCodes.push(newCode);
    }

    // Send all codes comma-separated.
    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount: existingCodes.join(',') })
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    // Refresh cart drawer so discounted prices appear.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.refresh) await cartDrawer.refresh();

    // Update banner to "applied" state.
    if (banner) {
      banner.classList.add('bundle-cart-banner--applied');
      const title = banner.querySelector('.bundle-cart-banner__title');
      if (title) title.textContent = 'Discount Applied!';
      const desc = banner.querySelector('.bundle-cart-banner__desc');
      if (desc) desc.textContent = 'Your bundle discount has been applied to the cart.';
    }
    button.textContent = 'Applied ✓';
  } catch (err) {
    console.error('[bundle-cart-banner] discount apply failed:', err);
    button.disabled = false;
    button.textContent = 'Apply Code →';
  }
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-apply-discount]');
  if (btn) applyBundleDiscount(btn);
});

/* ── Auto-apply on cart drawer open ──
   Banner presence already means the bundle is complete (≥2 line items sharing
   a `_bundle_campaign` code) and the code isn't applied yet — that check lives
   in bundle-cart-banner.liquid. Re-using the rendered banners as the source of
   truth keeps the auto-apply condition identical to the banner's render
   condition. The banner transitions through loading → applied states in
   place (kept in the DOM so the user sees what happened) while the cart
   drawer refreshes its prices/summary in the background. */

let autoApplyInFlight = false;

function setBannerLoading(banner) {
  banner.classList.add('bundle-cart-banner--loading');
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) {
    if (btn.dataset.originalLabel == null) {
      btn.dataset.originalLabel = btn.textContent.trim();
    }
    btn.disabled = true;
    btn.textContent = 'Applying…';
  }
}

function setBannerApplied(banner) {
  banner.classList.remove('bundle-cart-banner--loading');
  banner.classList.add('bundle-cart-banner--applied');
  const title = banner.querySelector('.bundle-cart-banner__title');
  if (title) title.textContent = 'Discount Applied!';
  const desc = banner.querySelector('.bundle-cart-banner__desc');
  if (desc) desc.textContent = 'Your bundle discount has been applied to the cart.';
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Applied ✓';
  }
}

function revertBannerLoading(banner) {
  banner.classList.remove('bundle-cart-banner--loading');
  const btn = banner.querySelector('[data-apply-discount]');
  if (btn) {
    btn.disabled = false;
    if (btn.dataset.originalLabel) btn.textContent = btn.dataset.originalLabel;
  }
}

async function autoApplyVisibleBundleBanners() {
  if (autoApplyInFlight) return;

  const banners = Array.from(document.querySelectorAll(
    '[data-bundle-cart-banner]:not(.bundle-cart-banner--applied):not(.bundle-cart-banner--loading)'
  ));
  if (!banners.length) return;

  const bannerCodes = [];
  for (const banner of banners) {
    const code = banner.dataset.code;
    if (code && !bannerCodes.includes(code)) bannerCodes.push(code);
  }
  if (!bannerCodes.length) return;

  const cartUpdateUrl = window.theme?.routes?.cartUpdate || '/cart/update.js';
  const cartUrl = window.theme?.routes?.cart || '/cart.js';

  // Paint the loading state immediately so the user sees the auto-apply
  // happening rather than a silent banner that suddenly flips to applied.
  for (const banner of banners) setBannerLoading(banner);

  autoApplyInFlight = true;
  try {
    const cartResponse = await fetch(cartUrl + '.js', { credentials: 'same-origin' });
    const cart = await cartResponse.json();

    const existingCodes = (cart.discount_applications || [])
      .filter(d => d.type === 'discount_code')
      .map(d => d.title);

    const merged = existingCodes.slice();
    const newlyAddedCodes = [];
    for (const code of bannerCodes) {
      if (!merged.includes(code)) {
        merged.push(code);
        newlyAddedCodes.push(code);
      }
    }

    if (newlyAddedCodes.length) {
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount: merged.join(',') })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    }

    // Flip each banner to the applied state — kept in the DOM so the user
    // sees the outcome. cart-drawer.refresh() only touches the cart-items
    // + summary regions (banner lives outside those), so the updated state
    // survives the price/summary refresh that runs next.
    for (const banner of banners) {
      if (bannerCodes.includes(banner.dataset.code)) setBannerApplied(banner);
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.refresh) await cartDrawer.refresh();
  } catch (err) {
    console.error('[bundle-cart-banner] auto-apply failed:', err);
    for (const banner of banners) revertBannerLoading(banner);
  } finally {
    autoApplyInFlight = false;
  }
}

document.addEventListener('on:cart-drawer:after-open', autoApplyVisibleBundleBanners);
